// useCalendarData.js
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const LS_KEY = "vox.events";
const OFFLINE_KEY = "vox.events.offline";
const EVT = "vox:events-updated";

export function useCalendarData() {
  const [events, setEvents] = useState([]);

  // ----------------------
  // Load
  // ----------------------
  async function load() {
    try {
      const offline = localStorage.getItem(OFFLINE_KEY) === "1";
      if (offline) {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) {
          setEvents(JSON.parse(cached));
          return;
        }
      }

      const res = await axios.get("/api/events");
      const list = Array.isArray(res.data) ? res.data : [];
      setEvents(list);

      try {
        localStorage.setItem(LS_KEY, JSON.stringify(list));
        localStorage.setItem(OFFLINE_KEY, "0");
      } catch {}
    } catch {
      try {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) setEvents(JSON.parse(cached));
        localStorage.setItem(OFFLINE_KEY, "1");
        toast.warning("Vox is offline. Using saved calendar.");
      } catch {}
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, []);

  // ----------------------
  // Mutations
  // ----------------------
  async function add(event) {
    try {
      const res = await axios.post("/api/events", event);
      const created =
        Array.isArray(res.data) ? res.data[0] : res.data;

      setEvents((prev) => {
        const list = [...prev, created];
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(list));
          localStorage.setItem(OFFLINE_KEY, "0");
        } catch {}
        return list;
      });

      toast.success("Event added to calendar.");
    } catch {
      const fallback = {
        ...event,
        id: crypto.randomUUID(),
      };

      setEvents((prev) => {
        const list = [...prev, fallback];
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(list));
          localStorage.setItem(OFFLINE_KEY, "1");
        } catch {}
        return list;
      });

      toast.warning("Event saved locally. Vox will sync later.");
    }

    window.dispatchEvent(new CustomEvent(EVT));
  }

  async function edit(event) {
    try {
      await axios.patch(`/api/events/${event.id}`, event);
      setEvents((prev) =>
        prev.map((e) => (e.id === event.id ? { ...e, ...event } : e))
      );
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify(
            events.map((e) =>
              e.id === event.id ? { ...e, ...event } : e
            )
          )
        );
      } catch {}

      toast.success("Event updated.");
    } catch {
      setEvents((prev) =>
        prev.map((e) => (e.id === event.id ? { ...e, ...event } : e))
      );
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
      } catch {}

      toast.warning("Updated locally. Vox will sync later.");
    }

    window.dispatchEvent(new CustomEvent(EVT));
  }

  async function remove(id) {
    try {
      await axios.delete(`/api/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Event removed.");
    } catch {
      setEvents((prev) => prev.filter((e) => e.id !== id));
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
      } catch {}

      toast.warning("Removed locally. Vox will sync later.");
    }

    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify(events.filter((e) => e.id !== id))
      );
    } catch {}

    window.dispatchEvent(new CustomEvent(EVT));
  }

  return { events, load, add, edit, remove };
}
